import React, { useState, useEffect, useMemo } from "react";

import { makeStyles } from "@mui/styles";

import CircularProgress from "@mui/material/CircularProgress";

import LinearProgress from "@mui/material/LinearProgress";
import FormControlLabel from "@mui/material/FormControlLabel";
import { Alert } from "@mui/material";

import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Card,
  CardHeader,
  CardActions,
  CardContent,
  Divider,
  Button,
  Grid,
  Switch,
  TextField,
  Typography,
  FormControl,
  FormHelperText,
  FormLabel,
  Radio,
  RadioGroup,
} from "@mui/material";

import clsx from "clsx";

import { useDispatch } from "react-redux";

import { useHistory } from "react-router-dom";
import {
  showErrorSnackbar,
  showSuccessSnackbar,
} from "store/actions/snackbarMessage";

import {
  getNewKioskCode,
  getKiosk,
  createKiosk,
  updateKiosk,
  getMerchantTerminals,
} from "apis/devicesAPIs";
import NewTables, { incompleteDineInTables } from "./NewTables";
import { noWheelInputProps } from "util/utils";
const CHARACTER_LIMIT = 30;

const useStyles = makeStyles((theme) => ({
  content: {
    flexGrow: 1,
    padding: theme.spacing(3),
  },
  toolbar: theme.mixins.toolbar,
  root: {},
  details: {
    display: "flex",
  },
  avatar: {
    height: 110,
    width: 100,
    flexShrink: 0,
    flexGrow: 0,
  },
  locationText: {
    paddingLeft: "15px",
  },
  buttonProperty: {
    position: "absolute",
    top: "50%",
  },
  uiProgess: {
    position: "fixed",
    zIndex: "1000",
    height: "31px",
    width: "31px",
    left: "50%",
    top: "35%",
  },
  progess: {
    position: "absolute",
  },
  uploadButton: {
    marginLeft: "8px",
    margin: theme.spacing(1),
  },
  customError: {
    color: "red",
    fontSize: "0.8rem",
    marginTop: 10,
  },
  submitButton: {
    marginTop: "10px",
  },
}));

const Kiosk = (props) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();

  const [firstName, setFirstName] = useState("");
  const [isMobilePhoneNumberRequired, setIsMobilePhoneNumberRequired] =
    useState(false);
  const [isNameRequired, setIsNameRequired] = useState(false);

  const [isPaymentRequired, setIsPaymentRequired] = useState(false);
  const [isPagerNumberRequired, setIsPagerNumberRequired] = useState(false);
  const [isCashAtCounterEnabled, setIsCashAtCounterEnabled] = useState(false);
  const [message, setMessage] = useState("");
  const [timeoutSeconds, setTimeoutSeconds] = useState(60);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [kioskNumber, setKioskNumber] = useState("");
  const [terminalNumber, setTerminalNumber] = useState("");
  const [selectedKiosk, setSelectedKiosk] = useState(null);
  const [qrTypes, setQrTypes] = useState([]);
  const [tables, setTables] = useState([]);

  const [buttonLoading, setButtonLoading] = useState(false);

  const queryClient = useQueryClient();

  // A Dine In entry with no table number saves an order the POS cannot place: it matches
  // dine-in orders to a table by exact table number, finds nothing, and drops the order.
  // The server rejects it too; blocking here is so the merchant sees why.
  const unfinishedDineInTables = incompleteDineInTables(tables);

  const showError = (msg) => dispatch(showErrorSnackbar(msg));
  const showSuccessMessage = (msg) => dispatch(showSuccessSnackbar(msg));

  const kioskId = props.match.params.kioskId;

  const getNewKioskNumber = async () => {
    try {
      setButtonLoading(true);
      const data = await getNewKioskCode();
      if (data) {
        setKioskNumber(data.newKioskNumber);
        setQrTypes(data.qrTypes);
      }
      setButtonLoading(false);
    } catch (err) {
      setButtonLoading(false);
      showError("Something went wrong! Please go back and try again.");
    }
  };

  const getKioskDetails = async () => {
    try {
      setButtonLoading(true);
      const kiosk = await getKiosk(kioskId);
      // console.log(JSON.stringify(kiosk));
      if (kiosk) {
        setSelectedKiosk(kiosk);
        setKioskNumber(kiosk.code);
        setTables(kiosk.tables);
        setTerminalNumber(kiosk.terminalNumber || "");
        setIsMobilePhoneNumberRequired(kiosk.isMobilePhoneNumberRequired);
        setIsNameRequired(kiosk.isNameRequired);
        setIsPaymentRequired(kiosk.isPaymentRequired);
        // Kiosks created before pager mode have neither field. Coerce so the Switch stays
        // controlled and an untouched kiosk keeps behaving exactly as it does today.
        setIsPagerNumberRequired(kiosk.isPagerNumberRequired === true);
        setIsCashAtCounterEnabled(kiosk.isCashAtCounterEnabled === true);
        setTimeoutSeconds(kiosk.timeoutSeconds);
        setMessage(kiosk.message);
        setQrTypes(kiosk.qrTypes);
      }
      setButtonLoading(false);
    } catch (err) {
      setButtonLoading(false);
      showError("Something went wrong! Please go back and try again.");
    }
  };

  const createNewKiosk = async () => {
    try {
      const kiosk = {
        isMobilePhoneNumberRequired,
        isNameRequired,
        timeoutSeconds: parseInt(timeoutSeconds),
        isPaymentRequired,
        isPagerNumberRequired,
        // Only meaningful while the kiosk takes payment; without it every order already
        // settles at the counter and the customer has nothing to choose between.
        isCashAtCounterEnabled: isPaymentRequired ? isCashAtCounterEnabled : false,
        code: kioskNumber,
        tables,
        terminalNumber,
        message,
      };

      setButtonLoading(true);
      await createKiosk(kiosk);
      history.push("/kioskDevices");
      setButtonLoading(false);
    } catch (err) {
      setButtonLoading(false);
      showError("Something went wrong! Please go back and try again.");
    }
  };

  const updateKioskDetails = async () => {
    try {
      const kiosk = {
        isMobilePhoneNumberRequired,
        isNameRequired,
        timeoutSeconds: parseInt(timeoutSeconds),
        isPaymentRequired,
        isPagerNumberRequired,
        // Only meaningful while the kiosk takes payment; without it every order already
        // settles at the counter and the customer has nothing to choose between.
        isCashAtCounterEnabled: isPaymentRequired ? isCashAtCounterEnabled : false,
        code: kioskNumber,
        tables,
        message,
        terminalNumber,
        id: kioskId,
      };

      setButtonLoading(true);
      const response = await updateKiosk(kiosk);
      showSuccessMessage("Kiosk updated successfully!");
      history.push(`/kioskDevices`);
      setButtonLoading(false);
    } catch (err) {
      setButtonLoading(false);
      showError("Something went wrong! Please go back and try again.");
    }
  };

  // Keyed on the kiosk being edited, not on `props`: react-router hands down a fresh
  // props object on every router update, and reloading the record on those discards
  // whatever the user has picked but not saved yet.
  useEffect(() => {
    if (!kioskId) {
      getNewKioskNumber();
    } else {
      getKioskDetails();
    }
    // getKioskDetails/getNewKioskNumber are redefined every render; kioskId is the
    // only real input.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kioskId]);

  const updateFormValues = (event) => {
    event.preventDefault();

    if (!selectedKiosk) {
      createNewKiosk();
    } else {
      updateKioskDetails();
    }
  };

  const handleSwitchChange = (event) => {
    const name = event.target.name;
    const value = event.target.checked;

    if (name === "isMobilePhoneNumberRequired") {
      setIsMobilePhoneNumberRequired(value);
      return;
    }
    if (name === "isNameRequired") {
      setIsNameRequired(value);
      return;
    }
    if (name === "isPaymentRequired") {
      setIsPaymentRequired(value);
      return;
    }
    if (name === "isPagerNumberRequired") {
      setIsPagerNumberRequired(value);
      return;
    }
    if (name === "isCashAtCounterEnabled") {
      setIsCashAtCounterEnabled(value);
      return;
    }
  };

  const {
    isLoading,
    isError,
    data: terminalData,
    error,
  } = useQuery({
    queryKey: ["terminalList"],
    queryFn: getMerchantTerminals
  });
  // The list only covers terminals Adyen has already moved into one of the restaurant's
  // stores. A terminal assigned from the terminal list is reassigned asynchronously - it
  // shows up here only once it next connects - and a reassigned one drops out. Without
  // its own option the native select renders blank, hiding a terminal the kiosk is
  // actually configured with, so carry the saved value into the list.
  const terminalOptions = useMemo(() => {
    const options = [];
    const seen = new Set();

    (terminalData || []).forEach((item) => {
      if (!item || !item.terminal || seen.has(item.terminal)) {
        return;
      }
      seen.add(item.terminal);
      options.push(item.terminal);
    });

    if (terminalNumber && !seen.has(terminalNumber)) {
      options.unshift(terminalNumber);
    }

    return options;
  }, [terminalData, terminalNumber]);

  if (isError) {
    return <Alert severity="error">{`${error.message} `}</Alert>;
  }

  if (isLoading) {
    return <CircularProgress size={150} className={classes.uiProgess} />;
  }

  return (
    <main className={classes.content}>
      {buttonLoading ? <LinearProgress /> : null}
      <Card className={clsx(classes.root, classes)}>
        <CardHeader title="Kiosk" subheader="Create or edit the Kiosk" />
        <form autoComplete="off" noValidate>
          <CardContent>
            <Grid container spacing={3} direction="column">
              <Grid item md={3} xs={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={isMobilePhoneNumberRequired}
                      onChange={handleSwitchChange}
                      name="isMobilePhoneNumberRequired"
                      color="primary"
                    />
                  }
                  label="Mobile phone number required"
                />
              </Grid>
              <Grid item md={3} xs={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={isNameRequired}
                      onChange={handleSwitchChange}
                      name="isNameRequired"
                      color="primary"
                    />
                  }
                  label="Name required"
                />
              </Grid>
              <Grid item md={3} xs={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={isPaymentRequired}
                      onChange={handleSwitchChange}
                      name="isPaymentRequired"
                      color="primary"
                    />
                  }
                  label="Payment required"
                />
              </Grid>
              <Grid item md={6} xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={isPagerNumberRequired}
                      onChange={handleSwitchChange}
                      name="isPagerNumberRequired"
                      color="primary"
                    />
                  }
                  label="Pager ordering mode"
                />
                <Typography variant="body2" color="textSecondary">
                  The pager number becomes the order identifier. The kiosk asks for it as
                  the last step before paying, so the customer collects a pager and then
                  pays holding it. It prints on the docket wherever &quot;Pager number&quot;
                  is switched on under Print Settings.
                </Typography>
              </Grid>
              <Grid item md={6} xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={isPaymentRequired && isCashAtCounterEnabled}
                      onChange={handleSwitchChange}
                      name="isCashAtCounterEnabled"
                      color="primary"
                      disabled={!isPaymentRequired}
                    />
                  }
                  label="Let the customer pay cash at the counter"
                />
                <Typography variant="body2" color="textSecondary">
                  {isPaymentRequired
                    ? "Asks the customer how they want to pay before the pager step. Cash orders skip the card terminal and are sent straight to the kitchen for the customer to settle at the counter."
                    : "Needs Payment required switched on. Without it the kiosk never takes payment, so every order is already settled at the counter."}
                </Typography>
              </Grid>
              <Grid item md={3} xs={6}>
                <TextField
                  fullWidth
                  label="Timeout (seconds)"
                  margin="dense"
                  name="timeoutSeconds"
                  variant="outlined"
                  type="number"
                  inputProps={noWheelInputProps}
                  value={timeoutSeconds}
                  onChange={(e) => setTimeoutSeconds(e.target.value)}
                />
              </Grid>
              <Grid item md={3} xs={6}>
                <FormControl component="fieldset" margin="dense" fullWidth>
                  <FormLabel component="legend" id="terminalNumber">
                    Terminal Number
                  </FormLabel>
                  <RadioGroup
                    aria-labelledby="terminalNumber"
                    name="terminalNumber"
                    value={terminalNumber}
                    onChange={(e) => setTerminalNumber(e.target.value)}
                  >
                    {terminalOptions.map((terminal) => (
                      <FormControlLabel
                        key={terminal}
                        value={terminal}
                        control={<Radio size="small" />}
                        label={terminal}
                      />
                    ))}
                    {/* Radios cannot be cleared by clicking them again, so a kiosk that
                        should take no terminal needs an option of its own. */}
                    <FormControlLabel
                      value=""
                      control={<Radio size="small" />}
                      label="None"
                    />
                  </RadioGroup>
                  {terminalOptions.length === 0 ? (
                    // Without this the field is just an empty box, which reads as a
                    // terminal that failed to load rather than one that was never
                    // assigned to the restaurant.
                    <FormHelperText>
                      No terminals are assigned to this restaurant yet. Assign one
                      on the Terminals page, then pick it here.
                    </FormHelperText>
                  ) : null}
                </FormControl>
              </Grid>

              <Grid item md={3} xs={6}>
                <TextField
                  className={classes.bottomButtons}
                  id="message"
                  label="Message (on the home screen)"
                  multiline
                  rows={4}
                  fullWidth
                  inputProps={{
                    maxlength: CHARACTER_LIMIT,
                  }}
                  value={message}
                  variant="outlined"
                  helperText={`${
                    message ? message.length : "0"
                  }/${CHARACTER_LIMIT}`}
                  placeholder="Enter message here"
                  onChange={(e) => setMessage(e.target.value)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>

              <Grid item md={3} xs={6}>
                <Typography>Kiosk Code: {kioskNumber}</Typography>
              </Grid>
              <NewTables
                qrTypes={qrTypes}
                updateTables={setTables}
                tableList={tables}
              />
            </Grid>
          </CardContent>
          <Divider />
          <CardActions />
        </form>
      </Card>

      <Button
        color="primary"
        variant="contained"
        type="submit"
        className={classes.submitButton}
        onClick={updateFormValues}
        disabled={
          buttonLoading ||
          !tables ||
          tables.length === 0 ||
          unfinishedDineInTables.length > 0 ||
          !kioskNumber
        }
      >
        Save Kiosk
        {buttonLoading && (
          <CircularProgress size={30} className={classes.progess} />
        )}
      </Button>

      {unfinishedDineInTables.length > 0 && (
        // Save sits below the table list, so on a kiosk with several entries the greyed-out
        // button is the only thing on screen. Say why here rather than only on the field.
        <FormHelperText error>
          Enter a table number for every Dine In type above before saving.
        </FormHelperText>
      )}
    </main>
  );
};

export default Kiosk;
